import React from "react";
import { Box } from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationRedriveAttempt,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  useInvestigationRedrives,
  useUninvestigatedGroups,
} from "../../hooks/useInvestigations";
import {
  RECONCILIATION_INTERVAL_MS,
  formatRelativeTime,
  formatTimestamp,
} from "./investigationUtils";

const timeOnly = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

const parse = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

/** The attempt being monitored: the one latestRedrive names, else the newest non-deletion. */
export const monitoredAttempt = (
  investigation: Pick<Investigation, "latestRedrive">,
  attempts?: InvestigationRedriveAttempt[] | null
): InvestigationRedriveAttempt | null => {
  if (!attempts?.length) return null;
  const id = investigation.latestRedrive?.attemptId;
  const named = id && attempts.find((a) => a.attemptId === id);
  if (named) return named;
  const redrives = attempts.filter(
    (a) => a.kind !== "DELETE" && a.kind !== "DOCUMENT_REDRIVE"
  );
  return (
    [...redrives].sort(
      (a, b) => (parse(b.startedAt) ?? 0) - (parse(a.startedAt) ?? 0)
    )[0] || null
  );
};

export interface MonitoringWatchStats {
  /** "12 items redriven 40 min ago" — what is being watched and since when. */
  redrive: string | null;
  /** "no returns yet" / "returns detected" */
  returns: string;
  /** "Next check ≈17:05" / "Next check due"; null before any reconciliation. */
  check: string | null;
  /** "Last checked 16:05" / "Not checked yet" — tooltip detail. */
  lastChecked: string | null;
}

/**
 * The watch stats for the Monitoring redrive callout. Returns are detected by the hourly
 * reconciliation (investigation_worker.reconcile), so the stage's `reconciledAt` is when Lusca
 * last looked; a reconciliation that ran before the redrive finished has not looked yet.
 */
export const monitoringWatchStats = (
  investigation: Pick<Investigation, "latestRedrive">,
  attempt: InvestigationRedriveAttempt | null,
  reconciledAt?: string | null,
  now: number = Date.now()
): MonitoringWatchStats => {
  const finished = parse(attempt?.completedAt);
  const startedIso =
    attempt?.startedAt ?? investigation.latestRedrive?.startedAt;
  let redrive: string | null = null;
  const count = attempt?.processedCount ?? attempt?.requestedCount;
  const verb = attempt?.kind === "DELETE" ? "deleted" : "redriven";
  const what =
    count !== undefined && count !== null
      ? `${count.toLocaleString()} item${count === 1 ? "" : "s"} ${verb}`
      : verb.charAt(0).toUpperCase() + verb.slice(1);
  if (finished !== null) {
    redrive = `${what} ${formatRelativeTime(attempt?.completedAt, now)}`;
  } else if (startedIso) {
    redrive = `Redrive started ${formatRelativeTime(startedIso, now)}`;
  }

  const returns =
    investigation.latestRedrive?.hasReturns || attempt?.hasReturns
      ? "returns detected"
      : "no returns yet";

  let check: string | null = null;
  let lastChecked: string | null = null;
  const last = parse(reconciledAt);
  if (last !== null) {
    const next = last + RECONCILIATION_INTERVAL_MS;
    check =
      next <= now ? "Next check due" : `Next check ≈${timeOnly.format(next)}`;
    // A reconciliation that ran before the redrive finished has not looked at it yet.
    const since = finished ?? parse(startedIso);
    lastChecked =
      since !== null && last < since
        ? "Not checked yet"
        : `Last checked ${timeOnly.format(last)}`;
  }
  return { redrive, returns, check, lastChecked };
};

/**
 * Quiet period before a monitored redrive counts as passed. Mirrors the backend's
 * DEFAULT_MONITORING_QUIET_HOURS (investigation_models.py); no stage overrides it today
 * (INVESTIGATION_MONITORING_QUIET_HOURS[_<STAGE>] is unset in CDK).
 */
export const MONITORING_QUIET_HOURS = 4;

/**
 * Earliest time the case can be marked ready to resolve: the redrive's completion plus the
 * quiet period. The flag itself is set by the first DLQ snapshot at or after this time that
 * shows no returns, so the real moment is up to one snapshot later. Null until it completes.
 */
export const monitoringReadyAt = (
  attempt: Pick<InvestigationRedriveAttempt, "completedAt"> | null
): number | null => {
  const finished = parse(attempt?.completedAt);
  return finished === null
    ? null
    : finished + MONITORING_QUIET_HOURS * 60 * 60 * 1000;
};

const dayAndTime = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/** "17:07 (in 3 h 40 min)" — the day is added when it isn't today. */
export const formatReadyAt = (at: number, now: number = Date.now()): string => {
  const sameDay = new Date(at).toDateString() === new Date(now).toDateString();
  const when = (sameDay ? timeOnly : dayAndTime).format(at);
  const minutes = Math.max(1, Math.round((at - now) / 60000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const left = h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
  return `${when} (in ${left})`;
};

interface MonitoringRedriveWatchProps {
  stage: Stage;
  investigation: Investigation;
}

/**
 * Details under the Monitoring redrive step: what was redriven and when, returns so far, and
 * when Lusca last looked — the evidence a human needs to decide when to resolve. Plain
 * secondary text rather than an alert: it annotates the step, it isn't a warning.
 */
export const MonitoringRedriveWatch: React.FC<MonitoringRedriveWatchProps> = ({
  stage,
  investigation,
}) => {
  // Same query keys as the Population and redrives tab and the Overview, so these are shared
  // cache reads, not extra calls, once the page has loaded.
  const redrives = useInvestigationRedrives(stage, investigation.id);
  const groups = useUninvestigatedGroups(stage, false);
  const attempt = monitoredAttempt(investigation, redrives?.data?.attempts);
  const stats = monitoringWatchStats(
    investigation,
    attempt,
    groups?.data?.reconciledAt
  );
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  // Two short one-line rows: what was redriven, then returns + next check. The step column is
  // narrow: the full "No returns yet · Next check ≈16:07" crowded into the Resolved column and
  // wrapping it made four ragged lines, so the second row uses a compact wording instead.
  const returnsShort =
    attempt?.kind === "DELETE"
      ? "verifying"
      : stats.returns === "returns detected"
      ? "returns detected"
      : "no returns";
  const checkShort = stats.check
    ?.replace("Next check ≈", "next ≈")
    .replace("Next check due", "check due");
  const first = stats.redrive ?? cap(returnsShort);
  const second = [stats.redrive ? returnsShort : null, checkShort]
    .filter(Boolean)
    .join(" · ");
  // Secondary detail lives in the tooltip so the step stays two short lines.
  const tooltip = [
    attempt?.completedAt && `Finished ${formatTimestamp(attempt.completedAt)}`,
    stats.lastChecked,
    attempt?.kind === "DELETE"
      ? "Items still in the DLQ reopen this investigation automatically."
      : "A detected return reopens this investigation automatically.",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Box
      color="text-body-secondary"
      fontSize="body-s"
      data-testid="monitoring-watch"
    >
      <div title={tooltip} style={{ whiteSpace: "nowrap" }}>
        <div data-testid="monitoring-watch-summary">{first}</div>
        {second && (
          <div data-testid="monitoring-watch-check">{cap(second)}</div>
        )}
      </div>
    </Box>
  );
};

export default MonitoringRedriveWatch;
